import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { reviewDetails } from "../../../../stores/actions/review.actions.types";
import { BiLoaderAlt } from "react-icons/bi";
import MetaHead from "../../../Common/Metahead";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";

const DetailReview = ({ reviewDetails, loading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [review, setReview] = useState(null);

  useEffect(() => {
    if (id) {
      reviewDetails({
        id,
        callback: (data) => {
          if (data?.success) {
            setReview(data?.data?.review);
          }
        },
      });
    }
  }, [id, reviewDetails]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <BiLoaderAlt className='animate-spin text-5xl text-primary dark:text-white' />
      </div>
    );
  }

  if (!review) {
    return (
      <div className='flex items-center justify-center min-h-screen text-gray-500 dark:text-gray-400'>
        No Review details available.
      </div>
    );
  }

  return (
    <div className='flex items-center justify-center p-4'>
      <MetaHead title={t("Review Details")} />
      <div className='bg-white dark:bg-boxdark rounded-lg p-6 w-full max-w-2xl relative shadow'>
        {/* Close Button */}
        <button
          className='absolute top-4 right-4 text-gray-500 dark:text-gray-400 text-xl'
          onClick={() => navigate(-1)}
        >
          <AiOutlineClose />
        </button>

        {/* Header */}
        <div className='mb-6'>
          <h2 className='text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-3'>
            Review Details
            <span
              className={`px-2 py-1 text-xs rounded-full capitalize ${
                review.status === "approved"
                  ? "bg-green-600 text-white"
                  : review.status === "rejected"
                  ? "bg-red-600 text-white"
                  : "bg-yellow-600 text-white"
              }`}
            >
              {review.status}
            </span>
          </h2>
        </div>

        {/* Review Info */}
        <div className='space-y-5 text-sm'>
          <div>
            <span className='font-medium text-gray-700 dark:text-gray-300'>
              Rating:
            </span>{" "}
            <span className='text-yellow-500 font-semibold'>
              {review.rating} ★
            </span>
          </div>

          <div>
            <span className='font-medium text-gray-700 dark:text-gray-300'>
              Comment:
            </span>
            <p className='text-gray-600 dark:text-gray-400 mt-1'>
              {review.comment || "No comment provided."}
            </p>
          </div>

          <div>
            <span className='font-medium text-gray-700 dark:text-gray-300'>
              Product:{" "}
              <Link
                className='text-xs text-blue-600'
                to={`/product/details/${review.productId?._id}`}
              >
                View Product
              </Link>
            </span>
            <p className='text-gray-600 dark:text-gray-400 mt-1'>
              {review.productId?.title || "N/A"}{" "}
            </p>
          </div>

          <div>
            <span className='font-medium text-gray-700 dark:text-gray-300'>
              User:
            </span>
            <p className='text-gray-600 dark:text-gray-400 mt-1'>
              <strong>Name : </strong>
              {review?.name ||
                (review?.firstName || review?.lastName
                  ? `${review?.firstName ?? ""} ${
                      review?.lastName ?? ""
                    }`.trim()
                  : "Unknown")}
              <br />
              <strong>Email : </strong>
              {review?.email ||
                (review.user?.email
                  ? `${review.user?.email ?? ""}`.trim()
                  : "Unknown")}
            </p>
          </div>

          <div>
            <span className='font-medium text-gray-700 dark:text-gray-300'>
              Created At:
            </span>
            <p className='text-gray-600 dark:text-gray-400 mt-1'>
              {new Date(review.createdAt).toLocaleString()}
            </p>
          </div>

          {/* Photos */}
          {review.photos?.length > 0 && (
            <div>
              <span className='font-medium text-gray-700 dark:text-gray-300'>
                Photos:
              </span>
              <div className='grid grid-cols-2 gap-3 mt-2'>
                {review.photos.map((photo, i) => (
                  <a
                    href={photo}
                    target='_blank'
                    rel='noopener noreferrer'
                    key={i}
                    className='block'
                  >
                    <img
                      src={photo}
                      alt={`review-photo-${i}`}
                      className='rounded-lg border border-gray-200 dark:border-gray-700 w-full h-32 object-cover'
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Videos */}
          {review.videos?.length > 0 && (
            <div>
              <span className='font-medium text-gray-700 dark:text-gray-300'>
                Videos:
              </span>
              <div className='grid grid-cols-1 gap-3 mt-2'>
                {review.videos.map((video, i) => (
                  <video
                    key={i}
                    controls
                    className='rounded-lg border border-gray-200 dark:border-gray-700 w-full'
                  >
                    <source src={video} type='video/mp4' />
                    Your browser does not support the video tag.
                  </video>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Update Button */}
        <Link
          to={`/review/update/${id}`}
          className='mt-6 block w-full bg-primary text-white py-2 rounded-lg text-center font-semibold hover:bg-primary/90 transition'
        >
          Update Review
        </Link>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({ loading: state?.app?.visible });
export default connect(mapStateToProps, { reviewDetails })(DetailReview);
